import React from 'react'
import FormBuilderCheckbox from "../Component/FormBuilderCheckbox"
import FormBuilderTextInput from "../Component/FormBuilderTextInput"
import FormBuilderSelection from "../Component/FormBuilderSelection"
import FormBuilderTextDisplay from "../Component/FormBuilderTextDisplay"
import FormBuilderTimePicker from "../Component/FormBuilderTimePicker"
import FormBuilderDatePicker from "../Component/FormBuilderDatePicker"
import FormBuilderImagePicker from "../Component/FormBuilderImagePicker"
import FormBuilderColorPicker from "../Component/FormBuilderColorPicker"
import FormBuilderFilePicker from "../Component/FormBuilderFilePicker"

/**
 * @typedef {import("react-hook-form").UseFormReturn} formPropsType
 * @typedef {import("i18next").TFunction<"translation",undefined>} i18nTFunctionType
 * 
 * @typedef {boolean?} defaultValueTriggerUpdateType - Forces an update with the default value on init, see useDefaultValueTriggerUpdate for details
 * @typedef {(value: any, fieldProps: formPropsType) => void} customOnChangeFnType - Custom on check function, called whenever the form onChange is triggered
 * @typedef {(e: React.KeyboardEvent) => void} customOnEnterDownFnType - Custom on enter down function
 * @typedef {(e: React.FocusEvent) => void} customOnFocusFnType - Custom on focus function
 * @typedef {() => void} customOnBlurFnType - Custom on blur function
 * @typedef {import("../lib/FormBuilderRules").definedRulesName[]?} rulesType - An array of rule name string from formRulesBuilder
 * @typedef {import("../lib/FormBuilderRules").Rule?} customRuleType - Custom rule
 * @typedef {string?} placeholderType - Placeholder
 * 
 * @typedef {Object} formBuilderSelectionOptionsType
 * @property {string} key
 * @property {string} title
 * @property {string} value
 * 
 * @typedef {import("../Component/FormBuilderFilePicker").formBuilderFilePickerFileFormValueType} formBuilderFilePickerFileFormValueType
 */

/**
 * @typedef {Object} baseFormComponentProps
 * @property {formPropsType} formProps
 * @property {i18nTFunctionType?} t
 */

/**
 * @typedef {Object} baseConfig
 * @property {string} fieldName - The field name (key) of the input in the form
 * @property {string} label - Displayed label of the input
 * @property {string?} subLabel - Displayed sub label of the input
 * @property {"keepPadding" | "removePadding"} [hideLabel] - Hide label options
 * @property {boolean} [mandatory=false] - Mark field as mandatory (will add a * to the label)
 * @property {boolean} [noErrorMessage=false] - Hide error message
 * @property {boolean} [disabled=false] - Disable the field
 * @property {boolean} [readOnly=false] - Readonly
 */

/**
 * @typedef {Object} checkboxConfig
 * @property {boolean?} defaultValue - Default checked value
 * @property {defaultValueTriggerUpdateType} defaultValueTriggerUpdate
 * @property {customOnChangeFnType} customOnCheckFn
 */

/**
 * @typedef {Object} selectionConfig
 * @property {string | string[]?} defaultValue - Default selected value, is an array if multiSelection is true
 * @property {defaultValueTriggerUpdateType} defaultValueTriggerUpdate
 * @property {rulesType} rules
 * @property {customRuleType} customRule
 * @property {formBuilderSelectionOptionsType[]} options
 * @property {boolean?} multiSelection - Allow multiple selection
 * @property {placeholderType} placeholder - Placeholder
 * @property {customOnChangeFnType} customOnChangeFn
 */

/**
 * @typedef {Object} textInputConfig
 * @property {string?} defaultValue - Default value
 * @property {defaultValueTriggerUpdateType} defaultValueTriggerUpdate
 * @property {number?} minLength - Minimum length
 * @property {number?} maxLength - Maximum length
 * @property {rulesType} rules
 * @property {rulesType?} onBlurRules - Rules to be triggered on blur
 * @property {customRuleType} customRule
 * @property {string?} inputType - Same as HTML input type, with the addition of "percentage"
 * @property {[number?, number?]} numberRange - [min, max], only works for number
 * @property {boolean?} asNumber - Cast to number before calling onChange and customOnChangeFn, will become string if not a number or is a incompleted decimal (e.g. 1.)
 * @property {boolean?} disableLineBreak - Disable line breaking
 * @property {boolean?} multiline - Enable multiline
 * @property {number?} rows - Number of rows, only works for multiline
 * @property {string?} placeholder - Placeholder
 * @property {customOnChangeFnType} customOnChangeFn
 * @property {customOnEnterDownFnType} customOnEnterDownFn
 * @property {customOnFocusFnType} customOnFocusFn
 * @property {customOnBlurFnType} customOnBlurFn
 */

/**
 * @typedef {Object} textDisplayConfig
 * @property {string?} displayValue - Overrides the form value display when provided
 * @property {((displayValue: string) => void)?} onClick - Called whenever the text is clicked, mutually exclusive with href
 * @property {string?} href - Set a link, mutually exclusive with onClick
 * @property {string?} fontVariant - Font variant, refer to fontStyles.js
 * @property {string?} prefix - Prefix to be prepended to the display value
 * @property {string?} suffix - Suffix to be appended to the display value
 */

/**
 * @typedef {Object} timePickerConfig
 * @property {Date?} defaultValue - Default value
 * @property {defaultValueTriggerUpdateType} defaultValueTriggerUpdate
 * @property {rulesType} rules
 * @property {customRuleType} customRule
 */

/**
 * @typedef {Object} datePickerConfig
 * @property {Date?} defaultValue - Default value
 * @property {defaultValueTriggerUpdateType} defaultValueTriggerUpdate
 * @property {("start" | "end")?} rangeType - Start or end of the range
 * @property {dayjs?} limitValue - Limit the date range, only works with rangeType
 * @property {dayjs?} minDate - Minimum date
 * @property {dayjs?} maxDate - Maximum date
 * @property {rulesType} rules
 * @property {customRuleType} customRule
 */

/**
 * @typedef {Object} imagePickerConfig
 * @property {string?} labelRemarks - Image uploader title remarks
 * @property {string?} importantRemarks - Image uploader important remarks
 * @property {string?} defaultImage - Default image (Imported image url)
 * @property {("image/jpeg" | "image/png")[]} allowedMimeTypes - Allowed mime types
 * @property {number?} maxSize - Max file size in bytes
 * @property {import("pages/merchant-management/merchants/offline-merchant-detail/Component/ImagePicker").dimensionConfig?} dimension
 */

/**
 * @typedef {Object} colorPickerConfig
 * @property {"hex"} colorType - Color type
 * @property {string?} defaultValue - Default value
 * @property {defaultValueTriggerUpdateType} defaultValueTriggerUpdate
 * @property {string?} placeholder - Placeholder
 * @property {customOnChangeFnType} customOnChangeFn
 */

/**
 * @typedef {Object} filePickerConfig
 * @property {boolean?} allowPreview - Allow file preview
 * @property {boolean?} allowMultiple - Allow multiple files
 * @property {formBuilderFilePickerFileFormValueType[]} [defaultFiles] - Default file config
 * @property {("application/pdf")[]} allowedMimeTypes - Allowed mime types
 * @property {number?} maxSize - Max file size in bytes
 * @property {string?} placeholder - Placeholder
 * @property {customOnChangeFnType} customOnChangeFn
 */

/**
 * @typedef {(
 *   (baseConfig & {fieldType: "checkbox", config?: checkboxConfig}) |
 *   (baseConfig & {fieldType: "selection", config?: selectionConfig}) |
 *   (baseConfig & {fieldType: "textInput", config?: textInputConfig}) |
 *   (baseConfig & {fieldType: "textDisplay", config?: textDisplayConfig}) |
 *   (baseConfig & {fieldType: "timePicker", config?: timePickerConfig}) |
 *   (baseConfig & {fieldType: "datePicker", config?: datePickerConfig}) |
 *   (baseConfig & {fieldType: "imagePicker", config?: imagePickerConfig}) |
 *   (baseConfig & {fieldType: "colorPicker", config?: colorPickerConfig}) | 
 *   (baseConfig & {fieldType: "filePicker", config?: filePickerConfig})
 * )} formBuilderFieldConfig
*/

/**
 * @param {Object} props 
 * @param {formPropsType} props.formProps
 * @param {i18nTFunctionType?} props.t
 * @param {formBuilderFieldConfig[]} props.fields
 */
const FormBuilderFieldBuilder = ({
  formProps,
  t,
  fields = [],
}) => {
  if (!formProps) throw new Error("formProps is required");
  return (fields.filter(Boolean).map((field) => {
    if (!field.config) field.config = {};
    const commonConfig = {
      formProps,
      t: t,
      fieldName: field.fieldName,
      label: field.label,
      subLabel: field.subLabel,
      hideLabel: field.hideLabel,
      mandatory: field.mandatory,
      noErrorMessage: field.noErrorMessage,
      disabled: field.disabled,
      readOnly: field.readOnly
    }
    switch (field.fieldType) {
      case "checkbox":
        return <FormBuilderCheckbox
          {...commonConfig}
          defaultValue={field.config.defaultValue}
          defaultValueTriggerUpdate={field.config.defaultValueTriggerUpdate}
          customOnCheckFn={field.config.customOnCheckFn}
        />
      case "selection":
        return <FormBuilderSelection
          {...commonConfig}
          defaultValue={field.config.defaultValue}
          defaultValueTriggerUpdate={field.config.defaultValueTriggerUpdate}
          rules={field.config.rules}
          customRule={field.config.customRule}
          options={field.config.options}
          multiSelection={field.config.multiSelection}
          customOnChangeFn={field.config.customOnChangeFn}
          placeholder={field.config.placeholder}
        />
      case "textInput":
        return <FormBuilderTextInput
          {...commonConfig}
          defaultValue={field.config.defaultValue}
          defaultValueTriggerUpdate={field.config.defaultValueTriggerUpdate}
          minLength={field.config.minLength}
          maxLength={field.config.maxLength}
          rules={field.config.rules}
          onBlurRules={field.config.onBlurRules}
          customRule={field.config.customRule}
          inputType={field.config.inputType}
          numberRange={field.config.numberRange}
          asNumber={field.config.asNumber}
          disableLineBreak={field.config.disableLineBreak}
          multiline={field.config.multiline}
          rows={field.config.rows}
          placeholder={field.config.placeholder}
          customOnChangeFn={field.config.customOnChangeFn}
          customOnEnterDownFn={field.config.customOnEnterDownFn}
          customOnFocusFn={field.config.customOnFocusFn}
          customOnBlurFn={field.config.customOnBlurFn}
        />
      case "textDisplay":
        return <FormBuilderTextDisplay
          {...commonConfig}
          displayValue={field.config.displayValue}
          onClick={field.config.onClick}
          href={field.config.href}
          fontVariant={field.config.fontVariant}
          prefix={field.config.prefix}
          suffix={field.config.suffix}
        />
      case "timePicker":
        return <FormBuilderTimePicker
          {...commonConfig}
          defaultValue={field.config.defaultValue}
          defaultValueTriggerUpdate={field.config.defaultValueTriggerUpdate}
          rules={field.config.rules}
          customRule={field.config.customRule}
        />
      case "datePicker":
        return <FormBuilderDatePicker
          {...commonConfig}
          defaultValue={field.config.defaultValue}
          defaultValueTriggerUpdate={field.config.defaultValueTriggerUpdate}
          rules={field.config.rules}
          customRule={field.config.customRule}
          rangeType={field.config.rangeType}
          limitValue={field.config.limitValue}
          minDate={field.config.minDate}
          maxDate={field.config.maxDate}
        />
      case "imagePicker":
        return <FormBuilderImagePicker
          {...commonConfig}
          labelRemarks={field.config.labelRemarks}
          importantRemarks={field.config.importantRemarks}
          defaultImage={field.config.defaultImage}
          allowedMimeTypes={field.config.allowedMimeTypes}
          maxSize={field.config.maxSize}
          dimension={field.config.dimension}
        />
      case "colorPicker":
        return <FormBuilderColorPicker
          {...commonConfig}
          colorType={field.config.colorType}
          defaultValue={field.config.defaultValue}
          defaultValueTriggerUpdate={field.config.defaultValueTriggerUpdate}
          rules={field.config.rules}
          customRule={field.config.customRule}
          customOnChangeFn={field.config.customOnChangeFn}
          placeholder={field.config.placeholder}
        />
      case "filePicker":
        return <FormBuilderFilePicker
          {...commonConfig}
          allowPreview={field.config.allowPreview}
          allowMultiple={field.config.allowMultiple}
          defaultFiles={field.config.defaultFiles}
          allowedMimeTypes={field.config.allowedMimeTypes}
          maxSize={field.config.maxSize}
          placeholder={field.config.placeholder}
          customOnChangeFn={field.config.customOnChangeFn}
        />
      default:
        return <>Please provide a valid field type</>
    }
  })

  )
}

export default FormBuilderFieldBuilder;